import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import type { CompanySummary } from '@core/types/api';
import { selectIsAuthenticated } from '@features/auth/store/authSlice';
import { selectActiveOrganizationUuid } from '@features/organization/store/organizationSlice';
import { useListCompaniesQuery } from './api/companyApi';
import { preferredCompanyUuid, selectActiveCompanyUuid, setActiveCompany } from './store/companySlice';

export interface UseActiveCompanyResult {
  /** The active company uuid (the default company once the list has loaded). */
  uuid: string | null;
  /** The active company, once it resolves against the loaded list. */
  company: CompanySummary | undefined;
  /** Every company of the active organization. */
  companies: CompanySummary[];
  /** Whether there is more than one company to switch between. */
  canSwitch: boolean;
  /** Whether the companies query is still in flight. */
  isLoading: boolean;
}

/**
 * Resolve the active company (the company every company-scoped request is filtered to).
 *
 * Self-healing: a persisted uuid that is not in the organization's list (another
 * organization's company, a deleted one) is replaced by the default company as soon as
 * the list arrives, so a stale `X-Company` never sticks. Until then the header may
 * still carry the stale uuid; `baseQuery` drops it on the first 403.
 */
export function useActiveCompany(): UseActiveCompanyResult {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const organizationUuid = useAppSelector(selectActiveOrganizationUuid);
  const activeUuid = useAppSelector(selectActiveCompanyUuid);
  const { data, isLoading } = useListCompaniesQuery(undefined, {
    skip: !isAuthenticated || !organizationUuid,
  });

  const companies = data ?? [];
  const isKnown = activeUuid !== null && companies.some((candidate) => candidate.uuid === activeUuid);
  const preferred = preferredCompanyUuid(companies);
  // Before the list loads, trust the persisted uuid: it is almost always right and
  // the header must go out with something. Once it has loaded, only a listed one.
  const uuid = data === undefined ? activeUuid : isKnown ? activeUuid : preferred;

  // Persist the healed / default selection so the next request carries it.
  useEffect(() => {
    if (data !== undefined && !isKnown && preferred) {
      dispatch(setActiveCompany(preferred));
    }
  }, [data, isKnown, preferred, dispatch]);

  const company = uuid ? companies.find((candidate) => candidate.uuid === uuid) : undefined;

  return {
    uuid,
    company,
    companies,
    canSwitch: companies.length > 1,
    isLoading,
  };
}
